// Helper type to capitalize string literals
type Capitalize<S extends string> = S extends `${infer First}${infer Rest}` ? `${Uppercase<First>}${Rest}` : S;

/**
 * Capitalizes the first letter of a string *
 * @example
 * ```ts
 * capitalize('hello') // 'Hello'
 * capitalize('world') // 'World'
 * ```
 */
export const capitalize = <S extends string>(str: S): Capitalize<S> =>
  (str.charAt(0).toUpperCase() + str.slice(1)) as Capitalize<S>;

/**
 * Normalizes a string by converting it to lowercase and trimming whitespace
 * @example
 * ```ts
 * normalizeString(' Hello ') // 'hello'
 * ```
 */
export const normalizeString = (str: string) => str.toLowerCase().trim();

/**
 * Collapses every run of whitespace into a single space, so text that differs only in spacing
 * compares as equal.
 * @example
 * ```ts
 * collapseWhitespace('best   pizza\tnear me') // 'best pizza near me'
 * ```
 */
export const collapseWhitespace = (str: string) => str.replace(/\s+/g, ' ');

/**
 * Joins an array of values into a single string, dropping `null`, `undefined`,
 * `false`, and empty strings so a separator never surrounds a blank entry.
 * @example
 * ```ts
 * joinNonEmptyValues(['London', '', 'UK'])   // 'London, UK'
 * joinNonEmptyValues(['a', 'b'], ' / ')      // 'a / b'
 * joinNonEmptyValues([null, undefined, ''])  // ''
 * ```
 */
export const joinNonEmptyValues = (values: Array<string | null | undefined | false>, separator = ', ') =>
  values.filter(Boolean).join(separator);
